import React from 'react';

import auth from '~/auth';

import { Alert, type AlertColor, Button } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import { type FailureCause, failureCause } from '~/utils/failureCause';

export interface FailureAlertProps {
  /** Cause-specific copy for this surface. Kept per caller; only the wrapper and the action are shared. */
  message: React.ReactNode;
  /** Pass the failed request's error directly, or a cause already classified by `failureCause`. */
  cause?: unknown;
  severity?: AlertColor;
  /** Omit when nothing can be retried (a generic-cause failure with no retry offered). */
  onRetry?: () => void;
  retryLabel?: string;
  retrying?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * One load-failure Alert shape for every governed-data surface: cause-specific copy, one action
 * that matches why the request actually failed. A lost session gets "Sign in", which starts the
 * same SSO redirect the sign-in button uses (`auth.startLogin`), because re-running the identical
 * request with no session is not a fix. Everything else gets the caller's own retry.
 */
export default function FailureAlert({
  message,
  cause,
  severity = 'error',
  onRetry,
  retryLabel = 'Retry',
  retrying = false,
  sx,
}: FailureAlertProps) {
  const resolvedCause: FailureCause = cause === 'auth' || cause === 'unknown' ? cause : failureCause(cause);

  const action =
    resolvedCause === 'auth' ? (
      <Button color="inherit" size="small" onClick={() => void auth.startLogin()}>
        Sign in
      </Button>
    ) : onRetry ? (
      <Button color="inherit" disabled={retrying} onClick={onRetry} size="small">
        {retrying ? 'Retrying…' : retryLabel}
      </Button>
    ) : undefined;

  return (
    <Alert action={action} severity={severity} sx={sx}>
      {message}
    </Alert>
  );
}
